import { getToken } from "./token-manager";

const API_URL = import.meta.env.VITE_API_URL;

export interface SSEEvent {
  type:
    | "text"
    | "tool_start"
    // Kory Code relay only: an in-progress refinement of a running tool call
    // (its real title and input, e.g. a Bash description). Merged in place.
    | "tool_update"
    | "tool_result"
    | "thinking_start"
    | "thinking_end"
    | "plan_ready"
    | "todo_complete"
    | "call_requested"
    | "sms_requested"
    | "email_requested"
    | "model_upgraded"
    | "permission_request"
    // Kory Code relay only: the relay auto-approved a residual permission
    // ask (agents run dangerous mode). Frozen informational card.
    | "permission_auto"
    // Kory Code relay only: the coding agent's todo list. Every event is a
    // FULL snapshot — ACP has no incremental plan ops — so the card re-renders
    // from `entries` rather than merging.
    | "plan"
    // Kory Code relay only: the agent is blocked on a structured question
    // (Claude's AskUserQuestion). The client answers via
    // POST /api/sessions/{id}/elicitation.
    | "elicitation_request"
    // Kory Code relay only: a relay-side advisory for the user (e.g. "this
    // branch's PR is already merged — prior work?"). Rendered inline in the
    // transcript as an informational callout.
    | "notice"
    | "model"
    | "models"
    | "links"
    | "usage"
    // Web Kory emission warning: a paused write raised its confirmation card;
    // `write_confirmation_pending` is the keepalive while the backend waits;
    // `_resolved` settles the card (allow / deny / expired); `_auto` is a write
    // an "Always approve for this chat" grant covered, shown already settled.
    | "write_confirmation"
    | "write_confirmation_pending"
    | "write_confirmation_resolved"
    | "write_confirmation_auto"
    // Web Kory: the relay is past its time budget on a turn that is still working (`content` is the line to show).
    | "working"
    // Kory session only: another surface's turn holds the session; resend after it.
    | "busy"
    // Kory session only: the to-do keys this reply points at so far (cumulative, in `todo_ids`).
    | "referenced_todos"
    // Kory session only: the reply's 2-3 next asks (in `asks`), the tray's chips; the latest replaces the last.
    | "next_asks"
    // Kory session only: open search or a page on the person's screen (`query`, `page`).
    | "open_search"
    // Web Kory relay: the accepted conversation, provider session, and turn
    // token. A first send has no conversation id until this event.
    | "conversation"
    | "done"
    | "error";
  content?: string;
  models?: Array<{
    id: string;
    label: string;
    group: string;
    efforts?: Array<"auto" | "low" | "medium" | "high" | "xhigh" | "max">;
    available?: boolean;
    unavailableReason?: string;
    resetsAt?: string;
    autoEligible?: boolean;
    versions?: string[];
    apiModel?: string;
  }>;
  todo_ids?: string[];
  /** `referenced_todos` only: the Overview sections this reply points at so far (`data-overview-section` keys). */
  sections?: string[];
  /** `referenced_todos` only: the other records this reply points at so far (`{type, id}` nouns). */
  nouns?: { type: string; id: string }[];
  /** `referenced_todos` only: what the reply's pointing numbered (`todo:<id>`, `<type>:<id>`, targets): the numbers Kory said. */
  order?: string[];
  /** `referenced_todos` only: the reply moved on, so the tray lets go of the last reply's cards. */
  released?: boolean;
  /** `next_asks` only: the asks the tray offers under the reply. */
  asks?: string[];
  /** `open_search` only: the words to search for, and a page by its menu name. */
  query?: string;
  page?: string;
  tool?: string;
  id?: string;
  tool_use_id?: string;
  // Provider event id. The client sends the latest one back as
  // `relayed_through` so the next reader window does not replay it.
  event_id?: string;
  // `conversation` / incomplete `done`: the binding for the next resume.
  conversation_id?: string;
  session_id?: string;
  turn_token?: string;
  scan_cursor?: string;
  // `done`: this resume was fenced because the turn was stopped.
  cancelled?: boolean;
  preview?: string;
  // On a `tool_result` event: the backend sets "error" when Managed Agents
  // flagged the tool result with is_error, else "complete".
  status?: "complete" | "error";
  // History responses serialize unset `input` as JSON `null` (Pydantic
  // default); the live `tool_start` event omits the key entirely. Accept
  // both rather than coercing on the wire.
  input?: Record<string, unknown> | null;
  // On `tool_start` from the Kory Code relay: the ACP tool category
  // (execute/read/edit/search/…).
  kind?: string | null;
  // On `tool_start` / `tool_result` from the Kory Code relay: per-call
  // router-acp attribution (candidate / routing reason / token usage).
  // `model` + `reason` already exist below for other events; these reuse them.
  tokens?: { input: number; output: number } | null;
  // Structured ACP content blocks (diff / text) preserved alongside the
  // flattened `preview` string so the action-card UI can render real diffs.
  contentBlocks?: Array<{
    type: string;
    path?: string;
    oldText?: string;
    newText?: string;
    content?: { type?: string; text?: string };
  }> | null;
  // Wall-clock stamp the relay puts on every logged event.
  ts?: string;
  // Payloads:
  //  plan_ready    — fired at the end of the planning turn. Carries the
  //                  checklist items + Continue-button label declared by
  //                  Claude's `plan` tool call.
  //  todo_complete — fired when the sub-agent calls `complete_todo`.
  //  call_requested — fired when the execute-phase `place_call` tool
  //                   (SOP `phone.call`) action handler returns
  //                   `ui: "open_call_dialog"`. Carries the **full**
  //                   payload (phone_number + script + purpose) so the
  //                   frontend can mount `<ClickToDialDialog>` pre-
  //                   populated. Separate from `tool_result` because the
  //                   preview field is 500-char-clipped and `script`
  //                   can easily exceed that.
  //  sms_requested  — fired when the execute-phase `send_sms` tool
  //                   (SOP `phone.sms`) action handler returns
  //                   `ui: "open_sms_dialog"`. Same shape — carries
  //                   the full unclipped body so `<SendSmsDialog>` can
  //                   pre-populate its editable Textarea. The `body`
  //                   can run up to ~1600 chars (RC MMS-converted
  //                   limit), well past the preview cap.
  summary?: string;
  todo_id?: string;
  items?: string[];
  continue_label?: string;
  // write_confirmation: how long the backend waits before the card expires.
  timeout_seconds?: number;
  // write_confirmation: false for Tech Spend writes, which always ask.
  can_remember?: boolean;
  // write_confirmation_resolved: the settled decision (allow/deny/expired).
  decision?: string;
  phone_number?: string;
  script?: string;
  purpose?: string;
  body?: string;
  todo_key?: string;
  //  email_requested — fired when the execute-phase `send_email` tool
  //                    (SOP `email.send`) handler returns
  //                    `ui: "open_email_dialog"`. Carries the recipient,
  //                    subject, and the full LLM-drafted body so
  //                    `<SendEmailDialog>` pre-populates its editable fields.
  to?: string;
  subject?: string;
  //  done — carries the resolved model id for the turn so the assistant
  //         message can render a model badge.
  model?: string;
  //  done — set when this reader window ended before the turn did (the
  //         wall-clock budget, most often). The client resumes the same
  //         provider turn; it does not treat the partial bubble as finished.
  //         Absent/false means a clean completion. `scan_cursor` is set only
  //         when the recovery scan itself did not finish.
  incomplete?: boolean;
  //  model_upgraded — fired once when the send transparently migrated the
  //                   conversation onto the user's newly preferred model.
  //                   Dedicated field names (not `to` above — that's the
  //                   email_requested recipient). Renders the inline
  //                   "Now using X" divider.
  from_model?: string;
  to_model?: string;
  //  permission_request — Kory Code relay only: the local coding agent needs
  //                       user approval before acting. `id` (above) carries
  //                       the request id; the client answers via
  //                       POST /api/sessions/{id}/permission.
  title?: string;
  options?: PermissionOption[];
  //  permission_request / permission_auto — the tool call this ask belongs to,
  //  so the client can anchor the card under that tool row (null when the ACP
  //  request carried no ref and no tool_start preceded it).
  toolCallId?: string | null;
  //  permission_auto — the option the relay auto-approved.
  optionId?: string;
  //  plan — the full todo-list snapshot.
  entries?: PlanEntry[];
  //  elicitation_request — the agent's prompt text and the JSON Schema of the
  //  form to render. `id` (above) carries the request id, `toolCallId` the
  //  tool row it belongs to.
  message?: string;
  schema?: ElicitationSchema;
  //  model — Kory Code relay only: the router pinned or switched the
  //          session's model. `model` (above) carries the candidate id;
  //          `reason` optionally explains the router's choice;
  //          `steering` reports whether that candidate implements
  //          `_session/steering` (optional for older relays).
  reason?: string | null;
  steering?: "supported" | "unsupported";
  //  links — Kory Code relay only: the session gained PR/ticket links
  //          (and possibly a new title derived from the ticket). `completed`
  //          marks the session's work as finished (e.g. its PR opened).
  links?: {
    pr?: { url: string; number: number };
    ticket?: { id: string; url: string; title?: string };
  };
  completed?: boolean;
  //  usage — Kory Code relay only: context-window usage during/after a turn.
  //          `used`/`size` are the context tokens consumed / window size
  //          (null when the agent doesn't report one); `total` is the
  //          session's cumulative token count, `estimated` when derived.
  used?: number | null;
  size?: number | null;
  total?: number;
  estimated?: boolean;
}

// A choice on a `permission_request` event (ACP permission option).
export interface PermissionOption {
  optionId: string;
  name: string;
  kind: string;
}

// One todo on a `plan` event (ACP PlanEntry).
export interface PlanEntry {
  content: string;
  priority: string;
  status: "pending" | "in_progress" | "completed" | string;
}

// One selectable choice on an elicitation field. `const` is the value written
// back; `title` is what the user reads (both are the option's label for
// AskUserQuestion, which records the label as the answer).
export interface ElicitationEnumOption {
  const: string;
  title?: string;
  description?: string;
  // Claude's AskUserQuestion carries each option's `preview` (a draft, a code
  // snippet, a mockup) here — what the user is approving by picking it.
  _meta?: {
    "_claude/askUserQuestionOption"?: { preview?: unknown };
  } & Record<string, unknown>;
}

// One form field of an `elicitation_request` schema. This is JSON Schema with
// primitive-typed properties: single-selects are strings carrying `enum` (bare
// values) or `oneOf` (titled options); multi-selects are arrays whose `items`
// carry the same two shapes.
export interface ElicitationProperty {
  type?: "string" | "number" | "integer" | "boolean" | "array" | string;
  title?: string;
  description?: string;
  default?: string | number | boolean | string[];
  enum?: string[];
  oneOf?: ElicitationEnumOption[];
  items?: { enum?: string[]; anyOf?: ElicitationEnumOption[] };
  minimum?: number;
  maximum?: number;
  // `_askUserQuestionCustomAnswer` marks a free-text field as the "Other" box
  // belonging to `questionId` — the adapters render it beside that question,
  // and a non-empty value overrides that question's selection.
  _meta?: {
    _askUserQuestionCustomAnswer?: { questionId?: string; isCustomAnswer?: boolean };
  } & Record<string, unknown>;
}

export interface ElicitationSchema {
  type?: string;
  title?: string;
  description?: string;
  properties?: Record<string, ElicitationProperty>;
  required?: string[];
}

// What the user filled in, keyed by property name (ACP ElicitationContentValue).
export type ElicitationContent = Record<string, string | number | boolean | string[]>;

/**
 * Read a long-lived GET SSE stream with the app's Bearer token. Use this, not
 * EventSource: EventSource can't send headers, and a JWT in the URL lands in
 * request logs. Yields each `data:` payload as a raw string and returns when
 * the server ends the stream; throws (with `status` on HTTP rejections) on
 * failure so the caller can back off.
 */
export async function* streamSSEGet(endpoint: string, signal?: AbortSignal): AsyncGenerator<string> {
  const token = getToken();
  const response = await fetch(`${API_URL}${endpoint}`, {
    headers: {
      Accept: "text/event-stream",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    signal,
  });
  if (!response.ok || !response.body) {
    const err = new Error(`Stream failed (${response.status})`) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        // `:` keepalive comments and blank separators carry no payload.
        if (line.startsWith("data: ")) yield line.slice(6);
      }
    }
  } finally {
    // Close the connection when the caller stops early (break / return), not
    // just on abort — otherwise the request stays open server-side.
    reader.cancel().catch(() => {});
  }
}

export async function* streamSSE(
  endpoint: string,
  body: object,
  signal?: AbortSignal,
  // Overrides for surfaces outside the main app (the Kory Code page streams
  // same-origin from its relay with its own JWT). Defaults keep every
  // existing caller on VITE_API_URL + the app token.
  opts?: {
    baseUrl?: string;
    token?: string | null;
    // Called once the server accepts the request and the response headers
    // arrive. This is intentionally separate from yielded data events: an SSE
    // connection can drop after the server accepted a turn but before its
    // first event is delivered.
    onConnected?: () => void;
  },
): AsyncGenerator<SSEEvent> {
  const token = opts?.token !== undefined ? opts.token : getToken();
  const response = await fetch(`${opts?.baseUrl ?? API_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    // Tag HTTP rejections with `http: true`. The caller uses this to tell a
    // genuine backend failure (auth, stale session, 5xx — show an error /
    // retry) apart from a mid-stream connection DROP (a thrown TypeError from
    // fetch/reader with no such tag), which is recoverable by reconciling
    // from persisted history.
    const tag = (err: Error): Error => {
      const tagged = err as Error & { http?: boolean; status?: number };
      tagged.http = true;
      tagged.status = response.status;
      return tagged;
    };
    if (response.status === 401) {
      throw tag(new Error("Unauthorized"));
    }
    const error = await response
      .json()
      .catch(() => ({ detail: "Stream failed" }));
    // FastAPI lets handlers return a dict `detail` (`raise HTTPException(detail={...})`)
    // so backend rejections can carry a structured `code` + `message` the UI
    // maps to an actionable CTA. Older bare-string `detail` payloads still
    // work — they just don't carry a code.
    if (error.detail && typeof error.detail === "object") {
      const err = new Error(error.detail.message || "Stream failed") as Error & {
        code?: string;
      };
      if (typeof error.detail.code === "string") {
        err.code = error.detail.code;
      }
      throw tag(err);
    }
    // The Kory Code relay serializes rejections as `{error, code?}` rather
    // than FastAPI's `{detail}` — surface its real message too.
    if (typeof error.error === "string" && error.error) {
      const err = new Error(error.error) as Error & { code?: string };
      if (typeof error.code === "string") err.code = error.code;
      throw tag(err);
    }
    throw tag(new Error(error.detail || "Stream failed"));
  }

  opts?.onConnected?.();
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            yield JSON.parse(line.slice(6)) as SSEEvent;
          } catch {
            // skip malformed events
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
